import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import {
  Flex,
  HStack,
  Text,
  VStack,
  useBreakpoint,
  Fade,
  Button,
  Stack,
  Select,
  Collapse,
  Wrap,
} from '@chakra-ui/react';
import {
  useGetAgeChartDataQuery,
  useGetKmChartDataQuery,
  useGetTotalChartDataQuery,
  useLazyGetAgeTableDataQuery,
  useLazyGetAverageKmTableDataQuery,
  useLazyGetKmTableDataQuery,
  useLazyGetTotalTableDataQuery,
} from '@spartalabs/pdc-core';
import { Card, ErrorCard, Loading, MapCard } from 'components';
import TableCard from 'components/Cards/TableCard';
import { DelayAnimation } from 'shared/DelayAnimation';
import {
  downloadFile,
  getBarsColor,
  getMapCardTitle,
  getMapColors,
  getRegionsOrStatesByRegion,
  getStatesByRegion,
  regionDtoToRegion,
  regionToRegionDto,
  validatesValue,
} from 'utils/functions';
import { ScrollButton } from '../../../components/ScrollButon/index';
import {
  totalTableLabels,
  ageTableLabels,
  kmTableLabels,
  averageKmTableLabels,
} from './consts';

const SCROLL_FORWARD_VALUE = 300;
const SCROLL_BACK_VALUE = -300;

function getTableLabels(type: string) {
  if (type === 'general') return totalTableLabels;

  if (type === 'age') return ageTableLabels;

  if (type === 'km') return kmTableLabels;

  if (type === 'averageKm') return averageKmTableLabels;

  return undefined;
}

function getAnalyticsId(type: string) {
  if (type === 'age') return 'Idade de todos os veículos';

  if (type === 'km') return 'Km de todos os veículos';

  if (type === 'averageKm') return 'Km médio de todos os veículos';

  return 'Quantidade de todos os veículos';
}

function getTableCardSubTitle(type: string) {
  if (type === 'age') return 'Distribuição de veículos por idade';

  if (type === 'km') return 'Distribuição de veículos por Km';

  if (type === 'averageKm') return 'Distribuição de veículos por Km médio';

  return ' Distribuição de veículos por quantidade';
}

export const FleetDist: React.FC = () => {
  const mainTableRef = useRef<string | undefined>();
  const HStackRef = useRef<HTMLDivElement>(null);
  const [type, setType] = useState('general');
  const [region, setRegion] = useState('Brazil');
  const [isScrollableLeft, setIsScrollableLeft] = useState(false);
  const [isScrollableRight, setIsScrollableRight] = useState(false);
  const breakpoint = useBreakpoint();

  //#region <CHARTS DATA AND REQUESTS>
  const {
    data: totalCharts,
    isLoading: isLoadingTotalCharts,
    isFetching: isFetchingTotalCharts,
    isError: isErrorTotalCharts,
    refetch: refetchTotalCharts,
  } = useGetTotalChartDataQuery({
    region: region === 'Brazil' ? undefined : region,
  });

  const { data: totalChartsSelect } = useGetTotalChartDataQuery({
    region: undefined,
  });

  const {
    data: ageCharts,
    isLoading: isLoadingAgeCharts,
    isFetching: isFetchingAgeCharts,
    isError: isErrorAgeCharts,
    refetch: refetchAgeCharts,
  } = useGetAgeChartDataQuery({
    region: region === 'Brazil' ? undefined : region,
  });

  const { data: ageChartsSelect } = useGetAgeChartDataQuery({
    region: undefined,
  });

  const {
    data: kmCharts,
    isLoading: isLoadingKmCharts,
    isFetching: isFetchingKmCharts,
    isError: isErrorKmCharts,
    refetch: refetchKmCharts,
  } = useGetKmChartDataQuery({
    region: region === 'Brazil' ? undefined : region,
  });

  const { data: kmChartsSelect } = useGetKmChartDataQuery({
    region: undefined,
  });

  const isLoadingCharts = useMemo(() => {
    if (type === 'general')
      return isLoadingTotalCharts || isFetchingTotalCharts;

    if (type === 'age') return isLoadingAgeCharts || isFetchingAgeCharts;

    if (type === 'km') return isLoadingKmCharts || isFetchingKmCharts;

    return false;
  }, [
    isLoadingTotalCharts,
    isFetchingTotalCharts,
    isLoadingAgeCharts,
    isFetchingAgeCharts,
    isLoadingKmCharts,
    isFetchingKmCharts,
  ]);

  const isErrorCharts = useMemo(() => {
    if (type === 'age') return isErrorAgeCharts;

    if (type === 'km') return isErrorKmCharts;

    return isErrorTotalCharts;
  }, [type, isErrorAgeCharts, isErrorKmCharts, isErrorTotalCharts]);

  const refetchCharts = useCallback(async () => {
    if (type === 'age') await refetchAgeCharts();

    if (type === 'km') await refetchKmCharts();

    await refetchTotalCharts();
  }, [type]);

  const chartsData = useMemo(() => {
    if (type === 'general') return totalCharts;

    if (type === 'age') return ageCharts;

    if (type === 'km') return kmCharts;

    return undefined;
  }, [totalCharts, ageCharts, kmCharts, type, region]);

  const chartsSelect = useMemo(() => {
    if (type === 'general') return totalChartsSelect;

    if (type === 'age') return ageChartsSelect;

    if (type === 'km') return kmChartsSelect;

    return undefined;
  }, [totalChartsSelect, ageChartsSelect, kmChartsSelect, type, region]);

  //#endregion

  //#region <TABLE DATA AND REQUESTS>
  const [
    getTotalTable,
    {
      data: totalTable,
      isLoading: isLoadingTotalTable,
      isFetching: isFetchingTotalTable,
      isError: isErrorTotalTable,
    },
  ] = useLazyGetTotalTableDataQuery();
  const [
    getAgeTable,
    {
      data: ageTable,
      isLoading: isLoadingAgeTable,
      isFetching: isFetchingAgeTable,
      isError: isErrorAgeTable,
    },
  ] = useLazyGetAgeTableDataQuery();
  const [
    getKmTable,
    {
      data: kmTable,
      isLoading: isLoadingKmTable,
      isFetching: isFetchingKmTable,
      isError: isErrorKmTable,
    },
  ] = useLazyGetKmTableDataQuery();
  const [
    getAverageKmTable,
    {
      data: averageKmTable,
      isLoading: isLoadingAverageKmTable,
      isFetching: isFetchingAverageKmTable,
      isError: isErrorAverageKmTable,
    },
  ] = useLazyGetAverageKmTableDataQuery();

  const fetchTable = useMemo(() => {
    if (type === 'averageKm') return getAverageKmTable;

    if (type === 'age') return getAgeTable;

    if (type === 'km') return getKmTable;

    return getTotalTable;
  }, [type]);

  const isLoadingTable = useMemo(() => {
    if (type === 'general') return isLoadingTotalTable || isFetchingTotalTable;

    if (type === 'age') return isLoadingAgeTable || isFetchingAgeTable;

    if (type === 'km') return isLoadingKmTable || isFetchingKmTable;

    if (type === 'averageKm')
      return isLoadingAverageKmTable || isFetchingAverageKmTable;

    return false;
  }, [
    isLoadingTotalTable,
    isFetchingTotalTable,
    isLoadingAgeTable,
    isFetchingAgeTable,
    isLoadingKmTable,
    isFetchingKmTable,
    isLoadingAverageKmTable,
    isFetchingAverageKmTable,
  ]);

  const isErrorTable = useMemo(() => {
    if (type === 'averageKm') return isErrorAverageKmTable;

    if (type === 'age') return isErrorAgeTable;

    if (type === 'km') return isErrorKmTable;

    return isErrorTotalTable;
  }, [
    type,
    isErrorAverageKmTable,
    isErrorAgeTable,
    isErrorKmTable,
    isErrorTotalTable,
  ]);

  const tableData = useMemo(() => {
    if (type === 'general') return totalTable;

    if (type === 'age') return ageTable;

    if (type === 'km') return kmTable;

    if (type === 'averageKm') return averageKmTable;

    return undefined;
  }, [totalTable, ageTable, kmTable, averageKmTable, type, region]);

  const { tableLabels, tableSubTitle, analyticsId } = useMemo(() => {
    return {
      tableLabels: getTableLabels(type),
      tableSubTitle: getTableCardSubTitle(type),
      analyticsId: getAnalyticsId(type),
    };
  }, [type]);
  //#endregion

  //#region <REFETCH USEEFFECT>
  useEffect(() => {
    if (type === 'general') {
      getTotalTable({
        page: 1,
        quantity: 5,
        term: mainTableRef.current,
        region: region === 'Brazil' ? undefined : region,
      });
    }

    if (type === 'age') {
      getAgeTable({
        page: 1,
        quantity: 5,
        term: mainTableRef.current,
        region: region === 'Brazil' ? undefined : region,
      });
    }

    if (type === 'km') {
      getKmTable({
        page: 1,
        quantity: 5,
        term: mainTableRef.current,
        region: region === 'Brazil' ? undefined : region,
      });
    }

    if (type === 'averageKm') {
      getAverageKmTable({
        page: 1,
        quantity: 5,
        term: mainTableRef.current,
      });
    }
  }, [type, region]);

  useEffect(() => {
    getTotalTable({
      page: 1,
      quantity: 5,
      term: '',
      region: '',
    });
  }, []);
  //#endregion

  const scrollHandler = useCallback(
    (dir?: string) => {
      if (dir === 'left') {
        HStackRef.current?.scrollBy({
          left: SCROLL_FORWARD_VALUE,
          behavior: 'smooth',
        });
      }

      if (dir === 'right') {
        HStackRef.current?.scrollBy({
          left: SCROLL_BACK_VALUE,
          behavior: 'smooth',
        });
      }

      if (!HStackRef.current || HStackRef.current?.scrollLeft <= 0) {
        setIsScrollableLeft(false);
      } else {
        setIsScrollableLeft(true);
      }

      if (
        (isScrollableLeft && !HStackRef.current) ||
        (HStackRef.current &&
          HStackRef.current?.scrollLeft + 1 + HStackRef.current?.clientWidth >=
            HStackRef.current?.scrollWidth)
      ) {
        setIsScrollableRight(false);
      } else {
        setIsScrollableRight(true);
      }
    },
    [HStackRef.current?.scrollLeft],
  );

  useLayoutEffect(() => {
    scrollHandler();
  }, [type, region, chartsData]);

  return (
    <DelayAnimation>
      <VStack
        px={{ base: '16px', md: '32px' }}
        pb="20px"
        flex={1}
        minH="full"
        fontSize="xl"
        spacing="20px"
      >
        <Card minH={0} w="100%">
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify="space-between"
          >
            <VStack align="start">
              <Text color="text.100" fontSize="16px" mb="4px">
                Selecione o tipo de dado
              </Text>
              <Text color="text.300" fontSize="12px" mb="22px">
                A escolha vai influenciar nos dados das informação
              </Text>
            </VStack>

            <Stack direction={{ base: 'column', sm: 'row' }}>
              <VStack spacing="4px" align="start" mr="24px">
                <Text color="text.300" fontSize="12px">
                  Selecione o filtro
                </Text>
                <Wrap h="100%">
                  <Button
                    size="sm"
                    isActive={type === 'general'}
                    variant="outlineGray"
                    onClick={() => setType('general')}
                  >
                    Quantidade
                  </Button>
                  <Button
                    size="sm"
                    isActive={type === 'age'}
                    variant="outlineGray"
                    onClick={() => setType('age')}
                  >
                    Idade
                  </Button>
                  <Button
                    size="sm"
                    isActive={type === 'km'}
                    variant="outlineGray"
                    onClick={() => setType('km')}
                  >
                    Km
                  </Button>
                  <Button
                    size="sm"
                    isActive={type === 'averageKm'}
                    variant="outlineGray"
                    onClick={() => setType('averageKm')}
                  >
                    Km Médio
                  </Button>
                </Wrap>
              </VStack>
              <Fade in={type !== 'averageKm'}>
                <VStack spacing="4px" align="start">
                  <Text color="text.300" fontSize="12px">
                    Selecione o filtro
                  </Text>
                  <Select
                    value={region}
                    onChange={e => setRegion(e.target.value)}
                    w="120px"
                    disabled={type === 'averageKm' || !chartsData}
                  >
                    <option value="Brazil">Brasil</option>
                    {Object.keys(chartsSelect ? chartsSelect.count : {}).map(
                      key => {
                        if (validatesValue(key, chartsSelect.count[key])) {
                          return (
                            <option key={key} value={key.toUpperCase()}>
                              {regionToRegionDto(key)}
                            </option>
                          );
                        }
                      },
                    )}
                  </Select>
                </VStack>
              </Fade>
            </Stack>
          </Stack>
        </Card>

        {isLoadingCharts ? (
          <Flex h="225px" w="100%" justify="center" align="center">
            <Loading />
          </Flex>
        ) : isErrorCharts ? (
          <ErrorCard
            h="225px"
            w="full"
            maxW="full"
            mx={{ base: '16px', md: '32px' }}
            refetch={refetchCharts}
          />
        ) : (
          <Flex
            w={{ base: 'calc(100% + 32px)', md: 'calc(100% + 64px)' }}
            position="relative"
            justify="center"
          >
            <Collapse
              in={type !== 'averageKm'}
              animateOpacity
              unmountOnExit
              style={{ width: '100%' }}
            >
              <Fade in={isScrollableLeft}>
                <Flex
                  animation="ease-in-out 2s"
                  position="absolute"
                  color="text.100"
                  left={0}
                  bottom="auto"
                  h="full"
                  align="center"
                  bg="linear-gradient(90deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);"
                  pl={{ base: '16px', md: '32px' }}
                >
                  <ScrollButton
                    diretion="left"
                    handleClick={() => scrollHandler('right')}
                  />
                </Flex>
              </Fade>

              <Fade in={isScrollableRight}>
                <Flex
                  position="absolute"
                  color="text.100"
                  right={0}
                  bottom="auto"
                  h="full"
                  align="center"
                  bg="linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%);"
                  pr={{ base: '16px', md: '32px' }}
                >
                  <ScrollButton
                    diretion="right"
                    handleClick={() => scrollHandler('left')}
                  />
                </Flex>
              </Fade>

              <HStack
                w="full"
                maxW="full"
                spacing="20px"
                overflowX={
                  isScrollableLeft || isScrollableRight ? 'scroll' : 'hidden'
                }
                px={{ base: '16px', md: '32px' }}
                ref={HStackRef}
                onScroll={() => scrollHandler()}
                sx={{
                  '&::-webkit-scrollbar': {
                    borderRadius: '8px',
                    backgroundColor: 'background',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'gray.100',
                  },
                }}
              >
                {type === 'averageKm' && (
                  <Flex
                    h="200px"
                    align="center"
                    justify="center"
                    alignSelf="center"
                  />
                )}
                {region === 'Brazil' && chartsData ? (
                  <MapCard
                    title="Regiões do Brasil"
                    subtitle="Um resumo de como estão distribuídos as suas frotas no momento"
                    mapColor={getMapColors(chartsData.count)}
                    mapW={breakpoint === 'base' ? '100%' : '250px'}
                    minW="40%"
                    h="100%"
                    hasMap
                    mapType={region}
                    hasBars={type === 'age' || type === 'km'}
                    barsColor={getBarsColor(region, type)}
                    data={
                      type === 'general'
                        ? getRegionsOrStatesByRegion(chartsData.count, region)
                        : chartsData.count.br
                    }
                    setSelected={val =>
                      setRegion(regionDtoToRegion(val).toUpperCase())
                    }
                    isMapClickable
                  />
                ) : chartsData ? (
                  <>
                    <MapCard
                      title={getMapCardTitle(region.toLowerCase())}
                      subtitle="Um resumo de como estão distribuídos as suas frotas no momento"
                      mapColor={getMapColors(chartsData.statesCount)}
                      mapW={breakpoint === 'base' ? '100%' : '250px'}
                      minW="40%"
                      h="100%"
                      hasMap
                      mapType={region}
                      hasBars={type === 'age' || type === 'km'}
                      barsColor={getBarsColor(region, type)}
                      data={
                        type === 'general'
                          ? getRegionsOrStatesByRegion(
                              chartsData.statesCount,
                              region,
                            )
                          : chartsData.count[region.toLocaleLowerCase()]
                      }
                    />
                  </>
                ) : null}

                {region === 'Brazil' && chartsData ? (
                  Object.keys(chartsData.count).map(key => {
                    if (validatesValue(key, chartsData.count[key])) {
                      return (
                        <MapCard
                          key={key}
                          title={getMapCardTitle(key)}
                          subtitle="Um resumo de como estão distribuídos as suas frotas no momento"
                          mapColor={getMapColors(
                            chartsData.statesCount,
                            undefined,
                          )}
                          mapW={breakpoint === 'base' ? '100%' : '250px'}
                          minW="40%"
                          h="100%"
                          hasMap
                          mapType={key}
                          hasBars={type === 'age' || type === 'km'}
                          barsColor={getBarsColor(region, type)}
                          data={
                            type === 'general'
                              ? getRegionsOrStatesByRegion(
                                  chartsData.statesCount,
                                  key.toUpperCase(),
                                )
                              : chartsData.count[key]
                          }
                          fromBrazil
                        />
                      );
                    }
                  })
                ) : chartsData ? (
                  Object.keys(
                    getStatesByRegion(chartsData.statesCount, region),
                  ).map(key => {
                    if (validatesValue(key, chartsData.statesCount[key])) {
                      return (
                        <MapCard
                          key={key}
                          title={getMapCardTitle(key)}
                          subtitle="Um resumo de como estão distribuídos as suas frotas no momento"
                          mapColor={getMapColors(chartsData.statesCount, key)}
                          mapW={breakpoint === 'base' ? '100%' : '250px'}
                          minW="40%"
                          h="100%"
                          hasMap
                          mapType={region}
                          hasBars={type === 'age' || type === 'km'}
                          barsColor={getBarsColor(region, type)}
                          data={
                            type === 'general'
                              ? getRegionsOrStatesByRegion(
                                  chartsData.statesCount,
                                  key,
                                )
                              : chartsData.statesCount[key]
                          }
                        />
                      );
                    }
                  })
                ) : (
                  <></>
                )}
              </HStack>
            </Collapse>
          </Flex>
        )}

        <TableCard
          ref={mainTableRef}
          customTitle={
            <Text
              alignSelf="center"
              color="text.100"
              fontSize="16px"
              fontWeight={400}
            >
              Relatório de <b>Veículos</b>
            </Text>
          }
          subtitle={tableSubTitle}
          w="full"
          data={tableData}
          labels={tableLabels}
          fetchDownload={async () => {
            if (type === 'general')
              await downloadFile('general', 'DistdeFrotaQuantidade');

            if (type === 'age') await downloadFile('age', 'DistdeFrotaIdade');

            if (type === 'km') await downloadFile('km', 'DistdeFrotaKm');

            if (type === 'averageKm')
              await downloadFile('averageKm', 'DistdeFrotaKmMedio');
          }}
          hasGlobalSearch
          loading={isLoadingTable}
          region={region === 'Brazil' ? undefined : region}
          analyticsId={analyticsId}
          fetchData={fetchTable}
          isError={isErrorTable}
          refetch={fetchTable}
        />
      </VStack>
    </DelayAnimation>
  );
};
